import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {filter, map, skip, take, takeUntil} from 'rxjs/operators';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {CommandService} from '@core/services/command.service';
import {Router} from '@angular/router';
import {Api} from '@shared/constants/common-field.constants';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {
    GetFieldSectionsAction
} from '@shared/store/actions/virtual-grid.actions';
import {select, Store} from '@ngrx/store';
import {Subject, Subscription} from 'rxjs';
import {PlatformDialogHelper} from '@shared/components/platform-tool/platform-tool-dialog-definition/models/platform-dialog-helper';
import {FileManagementService} from '@core/services/file-management.service';
import {SendInvitationCommand} from '@features/contacts/store/contact.command';
import {DialogModel} from '@shared/models/dialog.model';
import {DialogService} from '@shared/services/dialog-service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {permissionGridSourceTypes, platformUsersStatusCounts} from '@features/security/security.constants';
import {IVirtualGridCollectionState} from '@shared/store/reducers/virtual-grid.reducer';
import {getVirtualGridItemsMetaData} from '@shared/store/selectors/virtual-grid.selector';
import {IToastState} from '@shared/store/toast/toast-reducer';
import {getNotification} from '@shared/store/toast/toast-selectors';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {SetCurrentHelpNoteAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';

@Injectable({
    providedIn: 'root'
})

export class PlatformToolSecurityHandler implements IPlatformToolHandler {
    invalidateSubject: InvalidateSubject;
    ngUnsubscribe = new Subject<void>();
    sourceType = 'Platform-Contact-V2';
    magicLinkModel: MagicLinkModel;
    magicLinkModelValueChanged: EventEmitter<any>;
  private selectedTab: TabModel;
    private statusCountsSubscription: Subscription;
    private latestStatusCounts: { [key: string]: string };

    constructor(private fuseSplashScreenService: FuseSplashScreenService,
                private commandService: CommandService,
                private router: Router,
                private snackBarService: SnackBarService,
                private dialogService: DialogService,
                private fileManagementService: FileManagementService,
                private queryState: Store<IQueryState>,
                private platformSidebarStore: Store<PlatformSidebarState>,
                private virtualGridStore: Store<IVirtualGridCollectionState>,
                private toastStore: Store<IToastState>
    ) {
    }

    executeCommand(command: PlatformCommand, callBack?: any) {
        switch (command.name) {
            case 'SendInvitationCommand':
                this.inviteAnUserToOrbitax();
                break;
            case 'ExportNonLicensedUsersCommand':
                command.projectId = '[PROJECT_ID]';
                this.executeCommandToApi(command);
                break;
            default:
                this.executeCommandToApi(command);
                break;
        }
    }

    private executeCommandToApi(command: PlatformCommand) {
        if (command.api && command.api.length > 0 && !command.isDisabled) {
            this.fuseSplashScreenService.show();
            command = JSON.parse(JSON.stringify(command));
            this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
                    this.fuseSplashScreenService.hide();
                    if (commandResponse.fieldValues['PlatformDialogId']) {
                        this.commandService.dialogService.showPlatformDialogDefinition(command.fieldValues['PlatformDialogId'],
                            command.api, command).subscribe(state => {
                            if (state.action === 'OK') {
                                PlatformDialogHelper.setDialogDefinitionValues(command, state.data.input['PlatformDialog']);
                            }
                        });
                        return;
                    }
                    if (commandResponse.fieldValues['DownloadFileResponseModel']) {
                        const downloadResponseModel = commandResponse.fieldValues['DownloadFileResponseModel'];
                        const fileId = downloadResponseModel?.fileId;
                        this.fileManagementService.downloadClientFileWithId(fileId).subscribe(payload => {
                            this.fuseSplashScreenService.hide();
                            this.fileManagementService.openSaveFileWindow(payload, downloadResponseModel.fileName);
                        });
                    }
                    if (commandResponse.fieldValues['RedirectUrl']) {
                        this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
                    } else if (commandResponse.fieldValues['RefreshGridConfig']) {
                        this.refreshGridConfig(commandResponse.fieldValues['SourceType']);
                    } else {
                        this.invalidateSubject.invalidate();
                    }

                    if (commandResponse.fieldValues['SnackBarMessage']) {
                        const duration = commandResponse.fieldValues['SnackBarMessage'].length * 80;
                        this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage'], duration);
                        return;
                    }
                    if (commandResponse.fieldValues['SnackBarErrorMessage']) {
                        this.snackBarService.showError(commandResponse.fieldValues['SnackBarErrorMessage']);
                        return;
                    }
                    if (commandResponse.fieldValues['RedirectCommand']) {
                      this.executeCommand(commandResponse.fieldValues['RedirectCommand']);
                      return;
                    }
                },
                error => {
                    console.error('Failed ToolBarComponent command', error);
                    this.fuseSplashScreenService.hide();
                });
        }
    }

    setSelectedTab(selectedTab: TabModel): void {
      this.selectedTab = selectedTab;
      this.platformSidebarStore.dispatch(new SetCurrentHelpNoteAction({
        helpNoteId: "permissions-details",
      }));
      this.followStatusCounts(selectedTab);
    }

    private followStatusCounts(selectedTab: TabModel): void {
        this.statusCountsSubscription?.unsubscribe();
        this.statusCountsSubscription = undefined;
        this.latestStatusCounts = null;

        const counterId = this.magicLinkModel?.informationBar?.informationCounterId;
        const sourceType = selectedTab?.tabData?.['SourceType'];
        if (!counterId || !sourceType || !selectedTab.tabData[platformUsersStatusCounts.parameter]) {
            return;
        }

        this.statusCountsSubscription = this.virtualGridStore.pipe(
            select(getVirtualGridItemsMetaData, sourceType),
            skip(1),
            map(metaData => this.readStatusCounts(metaData)),
            filter(counts => !!counts)
        ).subscribe(counts => {
            this.latestStatusCounts = counts;
            this.applyStatusCounts(counts);
        });

        this.statusCountsSubscription.add(this.toastStore.pipe(
            select(getNotification, counterId),
            filter(notification => !!notification?.payload?.counter)
        ).subscribe(() => setTimeout(() => this.applyStatusCounts(this.latestStatusCounts))));
    }

    private readStatusCounts(metaData: { [key: string]: string }): { [key: string]: string } {
        if (!metaData) {
            return null;
        }
        const prefix = platformUsersStatusCounts.metaDataPrefix;
        const counts: { [key: string]: string } = {};
        Object.keys(metaData)
            .filter(key => key.startsWith(prefix))
            .forEach(key => counts[key.substring(prefix.length)] = metaData[key]);
        return Object.keys(counts).length > 0 ? counts : null;
    }

    private applyStatusCounts(counts: { [key: string]: string }): void {
        if (!counts) {
            return;
        }
        (this.magicLinkModel?.informationBar?.commands || []).forEach(command => {
            const count = counts[command.fieldValues?.['Key']];
            if (count !== undefined) {
                command.fieldValues['Text'] = count;
            }
        });
    }

    valueChanged($event) {
        // Only for Custom(Manage) Fields

      if ((this.selectedTab.tabData['SourceType'] === permissionGridSourceTypes.licensedUsers ||
        this.selectedTab.tabData['SourceType'] === permissionGridSourceTypes.nonLicensedUsers)
        && $event?.fieldDefinition?.isCustom) {
        if($event.action.id === 'Edit'){
          this.openEditor($event);
          return;
        }
        return;
      }
        // Other fields
        switch ($event.action) {
            case 'ManageApplicationRole':
                if ($event.sourceType == 'Manage-Roles') {
                  const manageRoleDialogCommand = new PlatformCommand('ManageApplicationRoleDialogCommand', Api.Security);
                  manageRoleDialogCommand.fieldValues['RoleName'] = $event.fieldDefinition.header;
                  manageRoleDialogCommand.fieldValues['RoleId'] = $event.fieldDefinition.key;
                  this.executeCommand(manageRoleDialogCommand);
                }
                return;
            case 'ModifyFeaturePermissionLevelOfARole':
                const modifyFeaturePermissionCommand = new PlatformCommand('ModifyCustomRoleFeaturePermissionCommand', Api.Security);
                modifyFeaturePermissionCommand.fieldValues['RoleId'] = $event.roleId;
                modifyFeaturePermissionCommand.fieldValues['FeatureId'] = $event.featureId;
                modifyFeaturePermissionCommand.fieldValues['PermissionLevel'] = $event.permissionLevel.toString();
                this.executeCommand(modifyFeaturePermissionCommand);
                return;
        }

        if ($event.action.id === 'InviteToOrbitax' || $event.action.id === 'ResendNotification') {
            this.inviteAnUserToOrbitax($event);
            return;
        }
        if ($event.action.id == 'Decline') {
          this.dialogService.showYesNoDialog('Confirm Decline', 'Are you sure you want to decline this request?').pipe().subscribe(state => {
            if (state && state.action === 'OK') {
              const declineDataPointAccessRequestCommand = new PlatformCommand('DeclineDataPointAccessRequestCommand', Api.Security);
              declineDataPointAccessRequestCommand.fieldValues['accessRequestId'] = $event.item.id;
              this.executeCommand(declineDataPointAccessRequestCommand);
              return;
            }
          });
        }
        if ($event.action.id === 'Approve') {
            //debugger;
            const approveAccessRequestCommand = new PlatformCommand('AcceptAccessRequestDialogCommand', Api.Security);
            approveAccessRequestCommand.fieldValues['accessRequestId'] = $event.item.id;
            this.executeCommand(approveAccessRequestCommand);
            return;
        }
        if ($event.action.id === 'Delete') {
            this.dialogService.showYesNoDialog('Confirm Delete', 'Are you sure you want to remove this user?').pipe().subscribe(state => {
                if (state && state.action === 'OK') {
                    this.deletePlatformUser($event);
                }
            });
        }

        if ($event?.fieldDefinition?.key !== 'Actions') {
            switch ($event.sourceType) {
                case permissionGridSourceTypes.licensedUsers:
                case permissionGridSourceTypes.nonLicensedUsers:
                    const isLicensedUser = $event.sourceType === 'Licensed-Users';
                    const manageUserCommandName = isLicensedUser ? 'ManageLicensedUserDialogCommand' : 'ManageNonLicensedUserDialogCommand';
                    const manageUserDialogCommand = new PlatformCommand(manageUserCommandName, Api.Security);
                    manageUserDialogCommand.fieldValues['PlatformUserId'] = $event.item.id;
                    manageUserDialogCommand.fieldValues['IsLicensedUser'] = isLicensedUser;
                    if (this.magicLinkModel.fieldValues['WorkspaceId']) {
                      manageUserDialogCommand.fieldValues['DestinationWorkspaceId'] = this.magicLinkModel.fieldValues['WorkspaceId'];
                      manageUserDialogCommand.fieldValues['SourceWorkspaceId'] = this.magicLinkModel.fieldValues['WorkspaceId'];
                    }
                    this.executeCommand(manageUserDialogCommand);
                    return;
                case permissionGridSourceTypes.dynamicRole:
                    const manageRoleDialogCommand = new PlatformCommand('ManageDynamicRoleDialogCommand', Api.Security);
                    manageRoleDialogCommand.fieldValues['RoleName'] = $event.item.cells['Name'].value;
                    this.executeCommand(manageRoleDialogCommand);
                    return;
            }
        }
    }

    openEditor(event: any) {
        const minWidth = '800px';
        const maxWidth = '1500px';
        let sourceType = this.magicLinkModel.fieldValues['SourceType'];
        this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth, sourceType).subscribe(state => {
            if (state.action === 'OK') {
                let value = event.item.cells[event.fieldDefinition.key]?.value;
                let editFieldCommand = this.getEditFieldCommand(value, event);
                this.executeCommandToApi(editFieldCommand);
            }
        });
    }

    private getEditFieldCommand(value: any, event: any) {
        let editFieldCommand;
        if (this.selectedTab.tabData['SourceType'] === permissionGridSourceTypes.licensedUsers) {
            editFieldCommand = new PlatformCommand('EditLicensedUserCustomFieldsCommand', Api.Security);
        }
        else if (this.selectedTab.tabData['SourceType'] === permissionGridSourceTypes.nonLicensedUsers) {
            editFieldCommand = new PlatformCommand('EditNonLicensedUserCustomFieldsCommand', Api.Security);
        }
        else{
            console.error("Could not determine custom field source type.");
            return;
        }
        editFieldCommand.fieldValues['NewValue'] = value;
        editFieldCommand.fieldValues['FieldKey'] = event.fieldDefinition.key;
        editFieldCommand.fieldValues['DataType'] = event.fieldDefinition.dataType;
        editFieldCommand.fieldValues['PlatformUserId'] = event.item.id;
        return editFieldCommand;
    }


    inviteAnUserToOrbitax(event?: any) {
        let selectedItem = null;
        if (event && event.item) {
            selectedItem = event.item;
        }
        const dialogModel: DialogModel = DialogModel.create(
            'Invite to Orbitax',
            'InviteToOrbitaxTransferBoxComponent', {selectedItem: selectedItem, sourceType: event?.sourceType ?? this.sourceType},
            '700px',
            '700px'
        );
        dialogModel.okCaption = 'Done';
        dialogModel.isFullscreen = true;
        this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
            if (res.action === 'OK') {
                this.getInviteMessage(res.data.input.selectedItems);
            }
        });
    }

    private getInviteMessage(selectedItems: any) {
        console.log('selectedItems:', selectedItems);
        const dialogModel: DialogModel = DialogModel.create(
            'Invite to Orbitax',
            'InviteToOrbitaxMessageBoxComponent', {message: ''},
            '700px',
            '700px'
        );
        dialogModel.okCaption = 'Send';
        this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
            if (res.action === 'OK') {
                this.sendInvite(selectedItems, res.data.input.message);
            }
        });
    }

    private sendInvite(selectedItems: any, message: any) {
        console.log('sending Invite:', selectedItems, message);
        const command = new SendInvitationCommand(selectedItems, message);
        this.fuseSplashScreenService.show();
        this.commandService.executeCommand(command).pipe(takeUntil(this.ngUnsubscribe)).subscribe(state => {
            if (state) {
                this.fuseSplashScreenService.hide();
                this.snackBarService.showInformation('Invitation sent successfully.');
                this.invalidateSubject.invalidate();
                this.updateTemplateCount();
            }
        });
    }

    public updateTemplateCount()
    {
        const updateTemplateCountCommand = new PlatformCommand('ManageUserTemplateCountUpdateCommand', Api.Security);
        const platformLinkId = this.magicLinkModel.id;
        updateTemplateCountCommand.fieldValues['PlatformLinkId'] = platformLinkId;
        this.executeCommand(updateTemplateCountCommand);

    }
    destroy(): void {
        this.statusCountsSubscription?.unsubscribe();
        this.statusCountsSubscription = undefined;
        this.invalidateSubject.close();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private refreshGridConfig(sourceType) {
        this.queryState.dispatch(new GetFieldSectionsAction(sourceType));
    }

    private deletePlatformUser($event: any) {
        const deletePlatformUserCommand = new PlatformCommand('DeletePlatformUserCommand', Api.Security);
        const platformLinkId = this.magicLinkModel.id;
        deletePlatformUserCommand.fieldValues['PlatformUserId'] = $event.item.id;
        deletePlatformUserCommand.fieldValues['PlatformLinkId'] = platformLinkId;
        this.executeCommand(deletePlatformUserCommand);
    }
}
